/**
 * Card template cache — pre-warms every deterministic card DOM node during an idle
 * window and hands out `cloneNode(true)` copies at render time instead of building
 * from scratch. `cloneNode` still deep-copies every SVG node/attribute, but cloning
 * a built node is cheaper than element-by-element construction — measured cost and
 * methodology: PERFORMANCE.md § Choreography frames stay clean; expensive prep moves to idle
 * windows.
 *
 * The deck is deterministic: 32 non-trump faces (4 suits × 8 ranks) and 3 back
 * colors, and at deal time no trump exists yet (trump is retrofitted at
 * declaration — see `cards.js#retrofitCardCanvas`). The deal itself always happens
 * pre-declaration (gameType null), so the deal-burst clone win is untouched by the
 * Null-inverts rule below. Only that fixed set is cacheable; anything trump-flagged,
 * face-down-at-creation, or effectively inverted (the dev flag, or a live Null round)
 * falls through to a fresh build.
 */
import { OPPONENT_BACK, PETROL_BACK } from "../constants.js";
import { effectiveCardStyle } from "../card-style.js";
import { scheduleIdle } from "../utils.js";
import { createCard } from "./cards.js";
import { createCardBack } from "./card-backs.js";

const SUITS = ["diamonds", "hearts", "spades", "clubs"];
const RANKS = ["seven", "eight", "nine", "ten", "jack", "queen", "king", "ace"];
const BACK_COLORS = [OPPONENT_BACK.LEFT, OPPONENT_BACK.RIGHT, PETROL_BACK];

// Module-level state — an intentional infrastructure exception (see
// js/CLAUDE.md § Module-level state exceptions): the warmed flag plus the two
// template maps are this module's whole reason to exist, not scattered game state.
let warmed = false;
const faceTemplates = new Map(); // suit+rank -> detached template node
const backTemplates = new Map(); // color -> detached template node

// why: ~8 faces per idle callback (tbt-reshaping slice 1 §3) keeps each batch's
// DOM build under threshold — 4 batches for the 32-card deck, then the 3 backs as
// one final batch.
const FACE_BATCH_SIZE = 8;

/**
 * Builds every cacheable template (32 faces + 3 backs) across several
 * idle-scheduled batches instead of one blocking loop — faces in groups of
 * FACE_BATCH_SIZE, then the 3 backs as one final batch. Idempotent — a no-op
 * once already warmed (or warming). The one-shot latch (`warmed`) is claimed
 * HERE, at entry, exactly like game-graph.js#warmGameGraph's — so a concurrent
 * call during an in-flight warm is a no-op rather than double-building. The
 * trade: cloneFaceTemplate/cloneBackTemplate can now be asked for a template
 * mid-warm, before its batch has landed; both null-guard the map lookup for
 * exactly that window — a genuine race the batching introduces, not routine
 * defensive coding (cloneBackTemplate already guarded this way; cloneFaceTemplate
 * below now mirrors it).
 *
 * The face builds pin `cardStyle: "classic"` rather than ride the ambient
 * default: clones are only ever handed out when `effectiveCardStyle()` is
 * classic (see isCacheableFace), but the first-ever warm can fire while a Null
 * round's state is still committed (the landing screen's idle warm after a
 * quit-to-menu) — an ambient build there would latch inverted templates for
 * the rest of the session.
 */
export function warmCardTemplates() {
  if (warmed) return;
  warmed = true;
  warmFaceBatch(allFaceKeys(), 0);
}

function allFaceKeys() {
  return SUITS.flatMap((suit) => RANKS.map((rank) => [suit, rank]));
}

function warmFaceBatch(keys, start) {
  const end = Math.min(start + FACE_BATCH_SIZE, keys.length);
  for (let i = start; i < end; i++) {
    const [suit, rank] = keys[i];
    faceTemplates.set(
      suit + rank,
      createCard(suit, rank, { cardStyle: "classic" }),
    );
  }
  if (end < keys.length) scheduleIdle(() => warmFaceBatch(keys, end));
  else scheduleIdle(warmBackBatch);
}

function warmBackBatch() {
  for (const color of BACK_COLORS) {
    backTemplates.set(color, createCardBack(color));
  }
}

/** True when `options` describes a card identical to its warmed template. */
function isCacheableFace(options) {
  return (
    options.faceUp !== false &&
    !options.isTrump &&
    !options.trumpReveal &&
    options.cardStyle == null &&
    effectiveCardStyle() === "classic"
  );
}

/**
 * Returns a clone of the warmed face template for `suit`/`rank` with per-instance
 * options applied, or `null` when the cache isn't warmed yet or `options` puts the
 * card outside the cacheable set (trump, trump-reveal, face-down, or a pinned/
 * inverted card style) — the caller falls through to a full `createCard` build.
 */
export function cloneFaceTemplate(suit, rank, options) {
  if (!warmed || !isCacheableFace(options)) return null;
  // Null-guarded like cloneBackTemplate below: `warmed` latches at
  // warmCardTemplates' entry now (batched build, above), so a real render can
  // land between batches and ask for a suit+rank this cache hasn't warmed yet.
  const template = faceTemplates.get(suit + rank);
  if (template == null) return null;
  const clone = template.cloneNode(true);
  if (options.code != null) clone.dataset.code = options.code;
  if (options.isLegal) clone.classList.add("legal");
  return clone;
}

/**
 * Returns a clone of the warmed back template for `color`, or `null` when the
 * cache isn't warmed yet or `color` isn't one of the 3 warmed colors. Backs carry
 * no per-instance options — callers set style.width/height/transform themselves.
 */
export function cloneBackTemplate(color) {
  const normalized = color === undefined ? PETROL_BACK : color;
  if (!warmed) return null;
  const template = backTemplates.get(normalized);
  return template == null ? null : template.cloneNode(true);
}

/**
 * Clears both warmed template maps, resets the one-shot latch, and re-schedules the warm from
 * scratch (card-preferences design § B) — the rank-picker's start-screen flip calls this so a
 * later deal doesn't clone stale U/O (or B/D) glyphs baked into a face template at the prior warm.
 * The warm fires during the start screen's own idle window, so a flip landing mid-warm leaves the
 * original batched chain (warmFaceBatch/warmBackBatch) still running alongside the restart —
 * traced benign: each batch step reads the pref live at execution time (createCard ->
 * addCornerLabels -> rankDisplay -> rankStyle()), and the localStorage write this function's
 * caller already made precedes any later batch step on the single-threaded event loop, so the
 * worst case is redundant idle work, never a stale glyph landing after invalidation.
 */
export function invalidateCardTemplates() {
  faceTemplates.clear();
  backTemplates.clear();
  warmed = false;
  // scheduleIdle, not a direct call: warmCardTemplates' first batch builds real card SVGs
  // (createCard), and this fires from the rank picker's click handler — a direct call would put
  // that construction on the same main-thread turn as the click, the exact "expensive prep during
  // an interaction" the idle-window discipline (docs/PERFORMANCE.md) exists to avoid. The original
  // warm is already idle-scheduled by its caller (game-start-ui.js); this keeps the restart on the
  // same footing.
  scheduleIdle(warmCardTemplates);
}
